/**
 * VendFactura Sync Service
 * Handles syncing pending offline sales to the server
 */
import {
  getAllPendingSales,
  markSaleSynced,
  removeSyncedSales,
  getPendingSalesCount,
} from './offlineDB';

let isSyncing = false;

/**
 * Sync all pending sales to the server
 * @param {object} api - Axios instance from AuthContext
 * @returns {{ synced: number, failed: number, errors: string[] }}
 */
export async function syncPendingSales(api) {
  if (isSyncing) return { synced: 0, failed: 0, errors: ['Sync already in progress'] };
  isSyncing = true;

  const result = { synced: 0, failed: 0, errors: [] };

  try {
    const pendingSales = await getAllPendingSales();
    if (pendingSales.length === 0) {
      return result;
    }

    // Try bulk sync first
    try {
      const shopId = pendingSales[0].shop_id;
      const response = await api.post(`/shops/${shopId}/sales/bulk-sync`, {
        sales: pendingSales.map(s => ({
          offline_id: s.offline_id,
          items: s.items,
          payment_method: s.payment_method,
          subtotal: s.subtotal,
          tax_total: s.tax_total,
          total: s.total,
          cash_received: s.cash_received,
          change_given: s.change_given,
          customer_name: s.customer_name,
          offline_created_at: s.created_at,
        })),
      });

      const syncResults = response.data.results || [];
      for (const sr of syncResults) {
        if (sr.success) {
          await markSaleSynced(sr.offline_id);
          result.synced++;
        } else {
          result.failed++;
          result.errors.push(sr.error || 'Unknown error');
        }
      }
    } catch (bulkError) {
      // Fall back to individual sync
      console.warn('Bulk sync failed, falling back to individual:', bulkError.message);
      for (const sale of pendingSales) {
        try {
          await api.post(`/shops/${sale.shop_id}/sales`, {
            items: sale.items,
            payment_method: sale.payment_method,
            subtotal: sale.subtotal,
            tax_total: sale.tax_total,
            total: sale.total,
            cash_received: sale.cash_received,
            change_given: sale.change_given,
            customer_name: sale.customer_name,
          });
          await markSaleSynced(sale.offline_id);
          result.synced++;
        } catch (err) {
          result.failed++;
          result.errors.push(err.response?.data?.detail || err.message);
        }
      }
    }

    // Clean up synced sales
    await removeSyncedSales();
  } finally {
    isSyncing = false;
  }

  return result;
}

/**
 * Get count of pending offline sales
 */
export async function getPendingCount() {
  return getPendingSalesCount();
}

/**
 * Check if sync is currently running
 */
export function isSyncInProgress() {
  return isSyncing;
}
